import React from 'react';
import { HelpCircle, MessageSquare, Phone, ShieldCheck, Clock, ExternalLink, Edit3 } from 'lucide-react';
import { MentorAreaConfig } from '../../types/mentorship';

interface MentorshipSupportPageProps {
  config: MentorAreaConfig;
  isMentorOrAdmin?: boolean;
  onOpenAdminPanel?: () => void;
}

export const MentorshipSupportPage: React.FC<MentorshipSupportPageProps> = ({
  config,
  isMentorOrAdmin = false,
  onOpenAdminPanel,
}) => {
  const support = config.support || {
    photoUrl: config.mentorAvatarUrl,
    whatsappNumber: '5511999999999',
    message: 'Olá! Preciso de ajuda com a mentoria ' + config.courseTitle,
    workingHours: 'Segunda a Sexta, das 09h às 18h',
  };

  const accentColor = config.branding.accentColor || '#0FFF55';
  const cleanPhone = (support.whatsappNumber || '').replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${cleanPhone || '5511999999999'}?text=${encodeURIComponent(support.message || 'Olá! Preciso de suporte.')}`;

  return (
    <div className="w-full min-h-[70vh] flex flex-col items-center justify-center gap-6 animate-fade-in select-none max-w-4xl mx-auto py-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-2 px-3 py-1 rounded-full bg-white/5 border border-white/10">
          <HelpCircle className="w-4 h-4" style={{ color: accentColor }} />
          <span>Suporte Direto ao Aluno</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight">
          Precisa de Ajuda?
        </h1>
        <p className="text-sm text-gray-400 mt-2 max-w-lg mx-auto">
          Fale diretamente com {config.mentorName} e nossa equipe pelo WhatsApp oficial da mentoria.
        </p>
      </div>

      {/* Main Support Card */}
      <div className="w-full bg-[#17181c] border border-white/10 rounded-3xl p-6 md:p-10 flex flex-col items-center text-center gap-6 shadow-2xl relative overflow-hidden">
        {/* Ambient Glow */}
        <div
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ backgroundColor: accentColor }}
        />

        {/* Support Avatar */}
        <div className="relative w-24 h-24 md:w-32 md:w-32 rounded-full p-1 border-2 border-white/20 shadow-2xl">
          <img
            src={support.photoUrl || config.mentorAvatarUrl}
            alt="Foto do Suporte"
            className="w-full h-full object-cover rounded-full"
          />
          <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-[#0FFF55] border-2 border-[#17181c] shadow" />
        </div>

        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
            Atendimento Exclusivo no WhatsApp
          </h2>
          <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5 mt-1">
            <Clock className="w-3.5 h-3.5 text-gray-500" />
            <span>{support.workingHours || 'Segunda a Sexta, das 09h às 18h'}</span>
          </p>
        </div>

        {/* WhatsApp Direct Action Button */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-[#25D366] hover:bg-[#20bd5a] text-black font-black text-sm uppercase tracking-wide shadow-2xl shadow-[#25D366]/20 hover:scale-105 active:scale-95 transition-all w-full max-w-sm"
        >
          <MessageSquare className="w-5 h-5 fill-black text-black" />
          <span>Falar no WhatsApp</span>
          <ExternalLink className="w-4 h-4 opacity-70" />
        </a>

        {isMentorOrAdmin && onOpenAdminPanel && (
          <button
            onClick={onOpenAdminPanel}
            className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-white transition-colors mt-2"
          >
            <Edit3 className="w-3.5 h-3.5" style={{ color: accentColor }} />
            <span>Editar foto e WhatsApp do Suporte</span>
          </button>
        )}
      </div>
    </div>
  );
};
